import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import { ChevronRight } from 'lucide-react';
import { MainLayout } from '../../../../../../../ui';
import { toast } from '../../../../../../../ui/toast';
import {
  getAssessment,
  getDetailedAssessmentResults,
  resultsPreScreen,
} from '../../../../../../../utils/api_service/assessment_api';
import { getBEIResults } from '../../../../../../../utils/api_service/Behavioral';
import {
  Assessment,
  AssessmentQuestionResult,
  AttemptSkill,
  PreScreenResults as PreScreenResultsType,
  User,
} from '../../../../../../../utils/types/assessment_types';
import { Tabs } from '../../../../tabs/tabs';
import { DetailedResults } from './Containers/DetailedResults';
import { PreScreenResults } from './Containers/PreScreenResults';
import { Summary } from './Containers/Summary';

interface BEIQuestionAnswer {
  questionId: string;
  question: string;
  answer: string;
  followupReason: string | null;
  videoFilename: string;
}

export interface BEIResultsData {
  assessmentId: string;
  userId: string;
  initialQuestion: string;
  attribute: string;
  questionAnswer: BEIQuestionAnswer[];
  confidence: number;
  score: number;
  followupCount: number;
  completed: boolean;
  starScore: {
    situation: number;
    task: number;
    action: number;
    result: number;
  };
  id: string;
}

export const AssessmentResultsDetails: React.FC = () => {
  const RESULTS_TABS = ['Summary', 'Detailed Results', 'Pre-Screen Results'];
  const [activeTab, setActiveTab] = useState<number>(0);
  const [beiResults, setBeiResults] = useState<BEIResultsData[] | null>(null);
  const [assessmentData, setAssessmentData] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [, setSkills] = useState<AttemptSkill[]>();
  const [, setDetailedQuestions] = useState<
    AssessmentQuestionResult[]
  >([]);
  const [, setStoredQuestions] = useState<
    AssessmentQuestionResult[]
  >([]);
  const params =
    useParams<{ assessmentId: string; userId: string; id: string }>();
  const { assessmentId, userId, id } = params;

  // Extract ID from URL path as fallback
  const getIdFromUrl = () => {
    const pathParts = window.location.pathname.split('/');
    // URL structure: /view-assessment-result/assessmentId/userId/id
    return pathParts[4] || id; // 4th index for the id parameter
  };

  const finalId = id || getIdFromUrl();
  const [, setUser] = useState<User>();
  const [, setAssessmentDetails] = useState<Assessment>();
  const [preScreenResults, setPreScreenResults] = useState<
    PreScreenResultsType[]
  >([]);

  const isMounted = React.useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    // Fetch detailed assessment results with proper error handling
    const fetchAssessmentData = async () => {
      if (!finalId) {
        if (isMounted.current) {
          setError('Assessment result ID not found in URL');
          setLoading(false);
        }
        return;
      }

      try {
        if (isMounted.current) setLoading(true);
        const response = await getDetailedAssessmentResults(finalId);

        if (response && response.questions && response.questions.length > 0) {
          if (!isMounted.current) return;
          setSkills(response.skills);
          const newDetailedQuestions: AssessmentQuestionResult[] = [];
          const newStoredQuestions: AssessmentQuestionResult[] = [];

          response.questions.forEach((result: AssessmentQuestionResult) => {
            const questionResult = result;
            let startTime: number = 0;
            let endTime: number = 0;
            if (questionResult.timeStarted && questionResult.timeCompleted) {
              startTime =
                new Date(
                  questionResult.timeStarted.toString().split(' ').join('T'),
                ).getTime() / 1000;
              endTime =
                new Date(
                  questionResult.timeCompleted.toString().split(' ').join('T'),
                ).getTime() / 1000;
            }
            questionResult.completionTime = endTime - startTime;
            newDetailedQuestions.push(questionResult);
            newStoredQuestions.push(questionResult);
          });

          setDetailedQuestions(newDetailedQuestions);
          setStoredQuestions(newStoredQuestions);
          setUser(response.user);

          // Fetch assessment details
          try {
            const assessmentDetailsResponse = await getAssessment(
              response.questions[0].assessmentId,
            );
            if (isMounted.current)
              setAssessmentDetails(assessmentDetailsResponse);
          } catch (assessmentError) {
            console.warn(
              'Failed to fetch assessment details:',
              assessmentError,
            );
          }

          // Set the new assessment data format for DetailedResults
          if (isMounted.current) setAssessmentData(response);
        } else {
          if (isMounted.current) setAssessmentData(null);
        }
      } catch {
        if (isMounted.current) {
          setAssessmentData(null);
          toast.error(
            'Failed to load assessment data. Some features may not be available.',
            {
              position: 'bottom-center',
            },
          );
        }
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    // Fetch pre-screen results with proper error handling
    const fetchPreScreenData = async () => {
      if (!finalId) {
        if (isMounted.current) setPreScreenResults([]);
        return;
      }

      try {
        const response = await resultsPreScreen(finalId);
        if (!response.ok) {
          throw new Error(
            `Network response was not ok: ${response.statusText}`,
          );
        }
        const data = await response.json();
        if (isMounted.current) setPreScreenResults(data);
      } catch {
        if (isMounted.current) setPreScreenResults([]);
      }
    };

    fetchAssessmentData();
    fetchPreScreenData();
  }, [finalId]);

  useEffect(() => {
    const fetchBEIResults = async () => {
      const pathParts = window.location.pathname.split('/');
      const manualAssessmentId = pathParts[2];
      const manualUserId = pathParts[3];

      const finalAssessmentId = assessmentId || manualAssessmentId;
      const finalUserId = userId || manualUserId;

      if (!finalAssessmentId || !finalUserId) {
        if (isMounted.current)
          setError('Assessment ID or User ID not found in URL');
        return;
      }

      try {
        if (isMounted.current) {
          setLoading(true);
          setError(null);
        }
        const response = await getBEIResults(finalAssessmentId, finalUserId);
        if (response.ok) {
          const data = await response.json();
          if (isMounted.current) setBeiResults(data);
        } else {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch BEI results');
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Unknown error occurred';
        if (isMounted.current) setError(errorMessage);
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    fetchBEIResults();
  }, [assessmentId, userId]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <Box sx={{ mt: 3 }}>
            <Summary
              beiResults={beiResults}
              assessmentData={assessmentData}
              loading={loading}
              error={error}
            />
          </Box>
        );
      case 1:
        return (
          <Box sx={{ mt: 3 }}>
            <DetailedResults
              beiResults={beiResults || []}
              assessmentData={assessmentData}
              loading={loading}
              error={error}
            />
          </Box>
        );
      case 2:
        return (
          <Box sx={{ mt: 3 }}>
            <PreScreenResults
              preScreenResults={preScreenResults}
              loading={loading}
              error={error}
            />
          </Box>
        );
      default:
        return (
          <Box sx={{ mt: 3 }}>
            <Summary
              beiResults={beiResults}
              assessmentData={assessmentData}
              loading={loading}
              error={error}
            />
          </Box>
        );
    }
  };

  return (
    <Stack spacing={2.5} sx={{ width: '100%', height: '100%' }}>
      {/* Breadcrumb Navigation */}
      <Box sx={{ pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Breadcrumbs
          separator={<ChevronRight size={14} color="#94a3b8" />}
          aria-label="breadcrumb"
        >
          <MuiLink
            component={RouterLink}
            to="/assessment-list"
            color="text.secondary"
            underline="hover"
            variant="body2"
          >
            Assessments
          </MuiLink>
          <MuiLink
            component={RouterLink}
            to={`/view-assessment/${assessmentId}`}
            color="text.secondary"
            underline="hover"
            variant="body2"
          >
            Details
          </MuiLink>
          <Typography variant="body2" color="text.primary" fontWeight={600}>
            Results
          </Typography>
        </Breadcrumbs>
      </Box>

      <MainLayout>
        <Box sx={{ width: '100%' }}>
          <Tabs
            tabTitles={RESULTS_TABS}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          {renderTabContent()}
        </Box>
      </MainLayout>
    </Stack>
  );
};
